import { useState, useEffect, useRef } from 'react';
import { AgGridReact } from "ag-grid-react";
import { withTranslation } from 'react-i18next';
// import { useDispatch } from "react-redux";

import { Box, Button, CardMedia, Typography } from '@mui/material';
import AppCard from "@plannel/components/ui/AppCard";
import { red } from '@mui/material/colors';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import SettingsIcon from '@mui/icons-material/Settings';
import { AttachMoney, TrendingUp } from '@mui/icons-material';
import { includes, isEmpty } from "lodash";

import dpStatusDashboardService from "@plannel/services/dp/dp-status-dashboard-service";
import userService from "@plannel/services/system/user-service";
import planConfigService from "@plannel/services/data/plan-config-service";
import DefaultGridSetting from '@plannel/components/aggrid/DefaultGridSetting';
import GridUtils from '@plannel/components/aggrid/GridUtils';
import ChartComposed from '@plannel/components/rechart/ChartComposed';
import DataState from "@plannel/components/aggrid/DataState"
import RatioBarCell from "@plannel/components/aggrid/RatioBarCell";
import NumberUtil from "@plannel/utils/number-util";
import colorUtil from "@plannel/pages/utils/color-util"
import codeUtil from "@plannel/pages/utils/code-util"
import dateUtil from "@plannel/utils/date-util";
import debug from "@plannel/utils/debug-util";
import Snackbar from '@plannel/components/Snackbar';
import DpAlertSettings from '../dp-modal/DpAlertSettings';
import axiosUtil from "@plannel/utils/axios-bigint";
 

/*
 * 수식 툴팁은 행 데이터에 번역문을 담지 않고 띄울 때 만든다.
 * 번역문을 state 에 굳히면 언어를 바꿔도 그대로라 새로 조회할 때(=새로고침)까지 옛 언어가 남는다.
 * 그리드 콜백은 columnDefs 를 만든 시점의 t 를 가둬 두므로, 호출 시점에 언어 번들을 읽는
 * gridValueL10N 을 쓴다.
 */
const expressionTooltip = params => {
  const expression = params?.data?.expression;
  if (isEmpty(expression)) {
    return "";
  }
  let translated = expression;
  for (const word of expression.split(/(?:[,-/\s[\]]+)/)) {
    if (word !== "") {
      translated = translated.replace(word, GridUtils.gridValueL10N(word));
    }
  }
  return translated;
};

const DpStatusDashboard = ({ t, title }) => { // viewName,
  // const reduxViewState = reduxUtil.getViewState(viewName);
  // const reduxDispatch = useDispatch();
  const userInfo = axiosUtil.jsonbigParse(localStorage.getItem("user"));

  const { snackBarMessageColor: { dataEmpty, networkError }, colorSet } = colorUtil;
  const { roles, buckets } = codeUtil;

  const [reload, setReload] = useState(false);
  const [alertSetOpen, setAlertSetOpen] = useState(false);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [planConfig, setPlanConfig] = useState({});
  const [userRoles, setUserRoles] = useState([]);
  const alertMessage = useRef("");
  //forecast
  const [chartForecast, setChartForecast] = useState([]);
  const [forecastGridApi, setForecastGridApi] = useState(null);
  const [forecastGridData, setForecastGridData] = useState([]);
  const [forecastErrorCount, setForecastErrorCount] = useState(null);
  const [dataErrorCount, setDataErrorCount] = useState(null);
  const [forecastComputeDate, setForecastComputeDate] = useState(null);
  const [bucket, setBucket] = useState(buckets.MONTH);
  const [version, setVersion] = useState(null);

  //trend
  const [chartTrend, setChartTrend] = useState([]);
  const [trendGridApi, setTrendGridApi] = useState(null);
  const [trendGridData, setTrendGridData] = useState([]);
  const [trendGrowthCount, setTrendGrowthCount] = useState(null);
  const [trendReductioniCount, setTrendReductioniCount] = useState(null);
  const [trendComputeDate, setTrendComputeDate] = useState(null);
  const [movingAverage, setMovingAverage] = useState(3)

  //demendplan
  const [chartDemand, setChartDemand] = useState([]);
  const [demandGridApi, setDemandGridApi] = useState(null);
  const [demandGridData, setDemandGridData] = useState([]);
  const [demandRevenueCount, setDemandRevenueCount] = useState(null);
  const [demandOthersCount, setDemandOthersCount] = useState(null);
  const [demandComputeDate, setDemandComputeDate] = useState(null);

  const chartSeries1 = [
    { id: "actualValue", field: "actualValue", text: t("salesActual"), type: "bar", typeOptions: { fill: colorSet(0), barSize: 20 }, orientation: "left", groupName: t("amt") },
    { id: "trendValue", field: "trendValue", text: t("salesActualTrend"), type: "line", groupName: t("amt"), typeOptions: { stroke: colorSet(1)} },
  ];

  const chartSeries2 = [
    { id: "actualValue", field: "actualValue", text: t("salesActual") , type: "bar", groupName: t("amt"), typeOptions: { fill: colorSet(0), barSize: 20 }, orientation: "left"},
    { id: "baselineValue", field: "baselineValue", text: t("baselineForecast"), type: "line", groupName: t("amt"), typeOptions: { stroke: colorSet(1)} },
    { id: "releasedVersionValue", field: "releasedVersionValue", text:t("confirmedForecast"), type: "bar", groupName: t("amt"), typeOptions: { fill: colorSet(2), barSize: 20 }  }
  ];
  const chartSeries3 = [
    { id: "actualValue", field: "actualValue", text: t("salesActual"), type: "bar", typeOptions: { fill: colorSet(0), barSize: 20 }, orientation: "left", groupName: t("amt") },
    { id: "financeValue", field: "financeValue", text: t("financeForecast"), type: "area", orientation: "left", groupName: t("amt"), typeOptions: {fill: colorSet(1), stroke: colorSet(1)} },
    { id: "managerValue", field: "managerValue", text: t("confirmedForecast"), type: "line", orientation: "left", groupName: t("amt"), typeOptions: { stroke: colorSet(2)}  },
    { id: "financeValueYearAgo", field: "financeValueYearAgo", text: t("financeForecast(YoY)"), type: "line", orientation: "left", groupName: t("amt"), typeOptions: { stroke: colorSet(3)}  },
  ];

  const percentFormatter = ({ value }) => {
    const formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 1 });
    return `${formatValue}%`;
  }

  const statusValueCellRenderer = ({ value }) => {
    return GridUtils.gridValueL10N(value);
  }

  const colTrendDefs = [
    { headerName: "itemCd", field: "itemCd", cellClass: 'stringType' },
    { headerName: "itemName", field: "itemName" },
    {
      headerName: " ", field: "rate", valueGetter: ({ data }) => data.rate,
      cellRenderer: RatioBarCell,
      // 증감률이라 0 을 기준으로 좌우로 뻗는다. 여기선 색이 **부호**를 뜻하므로 감소만 위험색을 주고,
      //   증가는 null 로 두어 공용 기본값(팔레트 대표색 단색)에 맡긴다.
      cellRendererParams: {
        min: -100, max: 100, labelPlacement: "none",
        color: (value) => (value < 0 ? red[300] : null),
      }
    },
    {
      headerName: "changeInSales", field: "rate", type: ["rightAligned"], 
      tooltipValueGetter: expressionTooltip, valueFormatter: percentFormatter
    }
  ];

  const colForecastErrorDefs = [
    { headerName: "itemCd", field: "itemCd" },
    { headerName: "itemName", field: "itemName" },
    { headerName: "forecastError", field: "rate", type: ["rightAligned"], 
      tooltipValueGetter: expressionTooltip, valueFormatter: percentFormatter 
    }
  ];

  const colDemandPlanDefs = [
    { headerName: "itemCd", field: "itemCd" },
    { headerName: "itemName", field: "itemName" },
    { headerName: "status", field: "status", valueFormatter: statusValueCellRenderer },
    { headerName: "deviation", field: "rate", type: ["rightAligned"], 
      tooltipValueGetter: expressionTooltip, valueFormatter: percentFormatter 
    },
  ];

  const handleAlertSettings = () => {
    setAlertSetOpen(!alertSetOpen);
  };

  const sort = (data) => {
    data.sort((a, b) => {
      return b.rate - a.rate;
    });
  }


  const retrieveForecastGraph = (itemCd) => {
    dpStatusDashboardService.getForecastGraph({ itemCd, versionId: version, standardDate: forecastComputeDate })
      .then((response) => {
        if (response?.data) {
          setChartForecast(response.data.graphData)
        } else {
          setSnackInfo({ open: true, severity: networkError, content: t("MSG_NetworkError") });
        }
      })
      .catch((e) => {
        debug(e);
      });
  }
  
  const retrieveDemandPlanGraph = (itemCd) => {
    dpStatusDashboardService.getDemandPlanGraph({ itemCd, standardDate: demandComputeDate })
      .then((response) => {
        if (response?.data) {
          setChartDemand(response.data.graphData);
        } else {
          setSnackInfo({ open: true, severity: networkError, content: t("MSG_NetworkError") });
        }
      })
      .catch((e) => {
        debug(e);
      });
  }

  const retrieveTrendGraph = (itemCd) => {
    dpStatusDashboardService.getTrendGraph({ itemCd, movingAverage, standardDate: trendComputeDate })
      .then((response) => {
        if (response?.data) {
          setChartTrend(response.data.graphData)
        } else {
          setSnackInfo({ open: true, severity: networkError, content: t("MSG_NetworkError") });
        }
      })
      .catch((e) => {
        debug(e);
      })
  }

  const setForecastData = (forecastData) => {
    const gridData = [];
    if (forecastData.message) {
      const { message } = forecastData;
      alertMessage.current = `${alertMessage.current} [${t("forecastErrorAlert")}] ${t(message.message, { cause: t(message.detail), title: t("forecastErrorAlert") })} \n`;
    } else {
      if (forecastData.gridData.length > 0) {
        sort(forecastData.gridData);
        for (const data of forecastData.gridData) {
          const row = {
            itemCd: data.itemCd,
            itemName: data.itemName,
            rate: data.rate,
            expression: data.expression,
          }
          gridData.push(row);
        }
        setForecastGridData(gridData)
        setForecastErrorCount(NumberUtil.formatNumber(forecastData.searchCount_1))
        setDataErrorCount(NumberUtil.formatNumber(forecastData.searchCount_2))
        setForecastComputeDate(forecastData.standardDate)
        setBucket(forecastData.bucket);
      } else {
        alertMessage.current = `${alertMessage.current} [${t("forecastErrorAlert")}] ${t("MSG_NoData")} \n`;
      }
    }
  }

  const setDemandPlanData = (demandData) => {
    const gridData = [];
    if (demandData.message) {
      const { message } = demandData;
      alertMessage.current = `${alertMessage.current} [${t("demandPlanAlert")}] ${t(message.message, { cause: t(message.detail), title: t("demandPlanAlert") })} \n`;
    } else {
      if (demandData.gridData.length > 0) {
        sort(demandData.gridData);
        for (const data of demandData.gridData) {
          const row = {
            itemCd: data.itemCd,
            itemName: data.itemName,
            rate: data.rate,
            status: data.status,
            expression: data.expression,
          }
          gridData.push(row);
        }
        setDemandGridData(gridData)
        setDemandRevenueCount(NumberUtil.formatNumber(demandData.searchCount_1))
        setDemandOthersCount(NumberUtil.formatNumber(demandData.searchCount_2))
        setDemandComputeDate(demandData.standardDate)
        setBucket(demandData.bucket);
      } else {
        alertMessage.current = `${alertMessage.current} [${t("demandPlanAlert")}] ${t("MSG_NoData")} \n`;
      }
    }
  }

  const setTrendData = (trendData) => {
    const gridData = [];
    if (trendData.message) {
      const { message } = trendData;
      alertMessage.current = `${alertMessage.current} [${t("salesTrendAlerts")}] ${t(message.message, { cause: t(message.detail), title: t("salesTrendAlerts") })} \n`;
    } else {
      if (trendData.gridData.length > 0) {
        sort(trendData.gridData);
        for (const data of trendData.gridData) {
          let rate = data.rate;
          if (rate > 100) {
            rate = 100;
          }
          if (rate < -100) {
            rate = -100;
          }
          const row = {
            itemCd: data.itemCd,
            itemName: data.itemName,
            rate,
            expression: data.expression,
          }
          gridData.push(row);
        }
        setTrendGridData(gridData)
        setTrendGrowthCount(NumberUtil.formatNumber(trendData.searchCount_1))
        setTrendReductioniCount(NumberUtil.formatNumber(trendData.searchCount_2))
        setTrendComputeDate(trendData.standardDate)
      } else {
        alertMessage.current = `${alertMessage.current} [${t("salesTrendAlerts")}] ${t("MSG_NoData")} \n`;
      }
    }
  }

  const retrieveStatusDashboard = (params) => {
    alertMessage.current = "";
    dpStatusDashboardService.getStatusDashboard(params)
    .then((response) => {
      if (response?.data) {
        for (const key of Object.keys(response.data)) {
          if (includes(userRoles, roles.APP_BF) && key === "forecast") {
            setForecastData(response.data[key])
          } 
          if (key === "salesTrend") {
            setTrendData(response.data[key])
          } 
          if (key === "demandPlan") {
            setDemandPlanData(response.data[key])
          }
          if (key === "standardVersion") {
            setVersion(response.data[key])
          }
        }
        if (alertMessage.current !== "") {
          setSnackInfo({ open: true, severity: dataEmpty, content: alertMessage.current });
        }
      } else {
        setSnackInfo({ open: true, severity: dataEmpty, content: t("MSG_NoData") });
      }
    })
    .catch((e) => {
      debug(e);
      setSnackInfo({ open: true, severity: networkError, content: t("MSG_NetworkError") });
    });
  } 

  const retrievePlanConfig = () => {
    const param = {
      moduleCd: "DP",
      activeFlg: true,
      configCd: "ERROR_THRESHOLD"
    };

    planConfigService.getConfigCd(param)
      .then((response) => {
        if (response?.data) {
          // setMessage("");
          setPlanConfig(response.data);
          setMovingAverage(response.data.objVal?.movingAverage)
          retrieveStatusDashboard(response.data);
        } else {
          retrieveStatusDashboard(planConfig);
        }
      }).catch(e => {
        debug(e);
      })
  };

  const dateFormatted = (date) => {
    const dateFormatted = new ZDate(date);
    if (codeUtil.isWeekBucket(bucket)) {
      return dateUtil.getWeekString(dateFormatted);
    } 
    return dateUtil.headerDateFormatter(dateFormatted, bucket);
  }

  const onSelectionChangedForecast = (params) => {
    const selectedRow = params.api.getSelectedRows()[0];
    retrieveForecastGraph(selectedRow.itemCd);
  };

  const onSelectionChangedTrend = (params) => {
    const selectedRow = params.api.getSelectedRows()[0];
    retrieveTrendGraph(selectedRow.itemCd);
  };


  const onSelectionChangedDemand = (params) => {
    const selectedRow = params.api.getSelectedRows()[0];
    retrieveDemandPlanGraph(selectedRow.itemCd);
  };

  const retrieveUserRoles = () => {
    userService.getUserAccessRoles({ username: userInfo.username })
      .then(
        (response) => {
          const userAccess = response.data;
          setUserRoles(userAccess?.roles);
        },
      )
      .catch(e => { debug(e); });
  };

  useEffect(() => {
    retrieveUserRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEmpty(userRoles)) {
      retrievePlanConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRoles]);

  useEffect(() => {
    if (reload) {
      retrievePlanConfig();
      setReload(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload])

  useEffect(() => {
    if (forecastGridApi && forecastGridData.length > 0) {
      forecastGridApi.forEachNode(node => {
        if (node.rowIndex === 0) {
          node.setSelected(true);
        }
      })
      DataState.initialize(forecastGridApi);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forecastGridApi, forecastGridData]);

  useEffect(() => {
    if (trendGridApi && trendGridData.length > 0) {
      trendGridApi.forEachNode(node => {
        if (node.rowIndex === 0) {
          node.setSelected(true);
        }
      })
      DataState.initialize(trendGridApi);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendGridApi, trendGridData]);


  useEffect(() => {
    if (demandGridApi && demandGridData.length > 0) {
      demandGridApi.forEachNode(node => {
        if (node.rowIndex === 0) {
          node.setSelected(true);
        }
      })
      DataState.initialize(demandGridApi);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demandGridApi, demandGridData]);

  const boxStyle = { width: "33%", mr: "0.5%", height: "100%", minHeight: 500 };
  const chartOptions = { minWidth: "33%", xAxisInterval: null, minHeight: 220, margin: { left: 30, top: 30, bottom: 10, right: 30 }, tooltipAnimation: false, isAmt: true };

  return (
    <>
      <Box sx={{ width: "100%", height: "50px" }}>
        <Button variant="contained"
          startIcon={<SettingsIcon />}
          size="medium"
          sx={{ float: "right", width: "180px", mt: 1 }}
          onClick={handleAlertSettings}
        >
          {t("alertSettings")}
        </Button>
      </Box>
      <Box id={"dp-status-db"} className={"grid-area"} sx={{ display: "flex", height: "calc(100% - 50px)" }}>
        <Box sx={boxStyle}>
          <Box sx={{ display: "flex", height: "25px", alignItems: "flex-end", justifyContent: "center" }}>
            <LeaderboardIcon fontSize={"medium"} sx={{ mb: 0.2 }} />
            <Typography variant="subtitle1" sx={{ pl: 1, textAlign: "center", fontWeight: 500 }}>
              {t("salesTrendAlerts")}
            </Typography>
          </Box>
          <Box>

          </Box>
          <Box sx={{ height: "calc(60% - 90px)" }}>
            {chartTrend.length > 0 ?
              <ChartComposed
                xAxis={'baseDate'}
                dataSeries={chartSeries1}
                data={chartTrend}
                options={chartOptions}
                referenceLine={{x: {title: t("current"), value: dateFormatted(trendComputeDate)}, y: null}}
                tooltipAnimation={false}
              /> :
              <Typography variant="subtitle1" sx={{ p: 3, height: "80%", textAlign: "center", fontWeight: 500}}>
              {t("noChartData")}
            </Typography>
            }
          </Box>
          <Box sx={{ height: "90px" }}>
            <Box sx={{ width: "100%", height: "40px" }}>
              <AppCard sx={{ display: "flex", height: "100%", minHeight: "30px", p: 0 }}>
                <CardMedia
                  sx={{ display: "flex", alignItems: "center", width: "50%", bgcolor: "#048BA8" }}
                >
                  <Typography variant="subtitle1" pl="10px" color="#fff">
                    {t("salesGrowthItem")}
                  </Typography>
                </CardMedia>
                <Box sx={{ display: "flex", width: "50%", alignItems: "center", textAlign: "right" }}>
                  <Typography
                    variant="subtitle1"
                    component="span"
                    ml="auto"
                    width="50%"
                    textAlign="right"
                    pr="10px"
                  >
                    {trendGrowthCount ?? 0}
                  </Typography>
                </Box>
              </AppCard>
            </Box>
            <Box sx={{ width: "100%", height: "40px", mt: 1 }}>
              <AppCard sx={{ display: "flex", height: "100%", minHeight: "30px", p: 0 }}>
                <CardMedia
                  sx={{ display: "flex", alignItems: "center", width: "50%", bgcolor: "#048BA8" }}
                >
                  <Typography variant="subtitle1" pl="10px" color="#fff">
                    {t("salesReductionItem")}
                  </Typography>
                </CardMedia>
                <Box sx={{ display: "flex", width: "50%", alignItems: "center", textAlign: "right" }}>
                  <Typography
                    variant="subtitle1"
                    component="span"
                    ml="auto"
                    width="50%"
                    textAlign="right"
                    pr="10px"
                  >
                    {trendReductioniCount ?? 0}
                  </Typography>
                </Box>
              </AppCard>
            </Box>
          </Box>
          <Box className="ag-theme-balham" sx={{ height: "calc(40% - 35px)", mt: 1 }}>
            <AgGridReact
              rowData={trendGridData}
              onGridReady={params => setTrendGridApi(params.api)}
              columnDefs={GridUtils.getColumnDefs(colTrendDefs)}
              {...DefaultGridSetting({ title })}
              defaultColDef={{
                flex: 1,
                minWidth: 30,
                suppressHeaderMenuButton: true,
                resizable: true
              }}
              rowSelection={{ mode: 'singleRow', checkboxes: false }}
              onSelectionChanged={onSelectionChangedTrend}
              groupDefaultExpanded={-1}
              groupHideOpenParents
            />
          </Box>
        </Box>
        { includes(userRoles, roles.APP_BF) ? 
          <Box sx={boxStyle}>
            <Box sx={{ display: "flex", height: "25px", alignItems: "flex-end", justifyContent: "center" }}>
              <TrendingUp fontSize={"medium"} sx={{ mb: 0.2 }} />
              <Typography variant="subtitle1" sx={{ pl: 1, textAlign: "center", fontWeight: 500 }}>
                {t("forecastErrorAlert")}
              </Typography>
            </Box>
            <Box sx={{ height: "calc(60% - 90px)" }}>
              {chartForecast?.length > 0 ?
                <ChartComposed
                  xAxis={'baseDate'}
                  // yAxis
                  dataSeries={chartSeries2}
                  data={chartForecast}
                  options={chartOptions}
                  referenceLine={{x: {title: t("current"), value: dateFormatted(forecastComputeDate)}, y: null}}
                  tooltipAnimation={false}
                /> :
                <Typography variant="subtitle1" sx={{ p: 3, height: "80%", textAlign: "center", fontWeight: 500}}>
                {t("noChartData")}
              </Typography>
              }
            </Box>
            <Box sx={{ height: "90px" }}>
              <Box sx={{ width: "100%", height: "40px" }}>
                <AppCard sx={{ display: "flex", height: "100%", minHeight: "30px", p: 0 }}>
                  <CardMedia
                    sx={{ display: "flex", alignItems: "center", width: "50%", bgcolor: "#048BA8" }}
                  >
                    <Typography variant="subtitle1" pl="10px" color="#fff">
                      {t("forecastErrorItem")}
                    </Typography>
                  </CardMedia>
                  <Box sx={{ display: "flex", width: "50%", alignItems: "center", textAlign: "right" }}>
                    <Typography
                      variant="subtitle1"
                      component="span"
                      ml="auto"
                      width="50%"
                      textAlign="right"
                      pr="10px"
                    >
                      {forecastErrorCount ?? 0}
                    </Typography>
                  </Box>
                </AppCard>
              </Box>
              <Box sx={{ width: "100%", height: "40px", mt: 1 }}>
                <AppCard sx={{ display: "flex", height: "100%", minHeight: "30px", p: 0 }}>
                  <CardMedia
                    sx={{ display: "flex", alignItems: "center", width: "50%", bgcolor: "#048BA8" }}
                  >
                    <Typography variant="subtitle1" pl="10px" color="#fff">
                      {t("dataErrorItem")}
                    </Typography>
                  </CardMedia>
                  <Box sx={{ display: "flex", width: "50%", alignItems: "center", textAlign: "right" }}>
                    <Typography
                      variant="subtitle1"
                      component="span"
                      ml="auto"
                      width="50%"
                      textAlign="right"
                      pr="10px"
                    >
                      {dataErrorCount ?? 0 }
                    </Typography>
                  </Box>
                </AppCard>
              </Box>
            </Box>
            <Box className="ag-theme-balham" sx={{ height: "calc(40% - 35px)", mt: 1 }}>
              <AgGridReact
                rowData={forecastGridData}
                onGridReady={params => setForecastGridApi(params.api)}
                columnDefs={GridUtils.getColumnDefs(colForecastErrorDefs)}
                {...DefaultGridSetting({ title })}
                defaultColDef={{
                  flex: 1,
                  minWidth: 30,
                  suppressHeaderMenuButton: true,
                  resizable: true
                }}
                rowSelection={{ mode: 'singleRow', checkboxes: false }}
                onSelectionChanged={onSelectionChangedForecast}
                groupDefaultExpanded={-1}
                groupHideOpenParents
              />
            </Box>
          </Box>
        : null }
        <Box sx={{ ...boxStyle, mr: "auto" }}>
          <Box sx={{ display: "flex", height: "25px", alignItems: "flex-end", justifyContent: "center" }}>
            <AttachMoney fontSize={"medium"} sx={{ mb: 0.2 }} />
            <Typography variant="subtitle1" sx={{ pl: 1, textAlign: "center", fontWeight: 500 }}>
            {t("demandPlanAlert")}
            </Typography>
          </Box>
          <Box sx={{ height: "calc(60% - 90px)" }}>
            {chartDemand.length > 0 ?
              <ChartComposed
                xAxis={'baseDate'}
                dataSeries={chartSeries3}
                data={chartDemand}
                options={chartOptions}
                referenceLine={{x: {title: t("current"), value: dateFormatted(demandComputeDate)}, y: null}}
                tooltipAnimation={false}
              /> :
              <Typography variant="subtitle1" sx={{ p: 3, height: "80%", textAlign: "center", fontWeight: 500}}>
              {t("noChartData")}
            </Typography>
            }
          </Box>
          <Box sx={{ height: "90px" }}>
            <Box sx={{ width: "100%", height: "40px" }}>
              <AppCard sx={{ display: "flex", height: "100%", minHeight: "30px", p: 0 }}>
                <CardMedia
                  sx={{ display: "flex", alignItems: "center", width: "50%", bgcolor: "#048BA8" }}
                >
                  <Typography variant="subtitle1" pl="10px" color="#fff">
                    {t("largeRevenueGapItems")}
                  </Typography>
                </CardMedia>
                <Box sx={{ display: "flex", width: "50%", alignItems: "center", textAlign: "right" }}>
                  <Typography
                    variant="subtitle1"
                    component="span"
                    ml="auto"
                    width="50%"
                    textAlign="right"
                    pr="10px"
                  >
                    {demandRevenueCount ?? 0}
                  </Typography>
                </Box>
              </AppCard>
            </Box>
            <Box sx={{ width: "100%", height: "40px", mt: 1 }}>
              <AppCard sx={{ display: "flex", height: "100%", minHeight: "30px", p: 0 }}>
                <CardMedia
                  sx={{ display: "flex", alignItems: "center", width: "50%", bgcolor: "#048BA8" }}
                >
                  <Typography variant="subtitle1" pl="10px" color="#fff">
                  {t("promotionItem")}
                  </Typography>
                </CardMedia>
                <Box sx={{ display: "flex", width: "50%", alignItems: "center", textAlign: "right" }}>
                  <Typography
                    variant="subtitle1"
                    component="span"
                    ml="auto"
                    width="50%"
                    textAlign="right"
                    pr="10px"
                  >
                    {demandOthersCount ?? 0}
                  </Typography>
                </Box>
              </AppCard>
            </Box>
          </Box>
          <Box className="ag-theme-balham" sx={{ height: "calc(40% - 35px)", mt: 1 }}>
            <AgGridReact
              rowData={demandGridData}
              onGridReady={params => setDemandGridApi(params.api)}
              columnDefs={GridUtils.getColumnDefs(colDemandPlanDefs)}
              {...DefaultGridSetting({ title })}
              defaultColDef={{
                flex: 1,
                minWidth: 30,
                suppressHeaderMenuButton: true,
                resizable: true
              }}
              rowSelection={{ mode: 'singleRow', checkboxes: false }}
              onSelectionChanged={onSelectionChangedDemand}
              groupDefaultExpanded={-1}
              groupHideOpenParents
            />
          </Box>
        </Box>
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <DpAlertSettings
        open={alertSetOpen}
        close={handleAlertSettings}
        reload={setReload}
        userRoles={userRoles}
      />
    </>
  )
};

export default withTranslation()(DpStatusDashboard);